import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../redux/store';
import { getContentData, getProbes, getLiteVersionContentData, saveHistory, getPreviewContentById, getRating ,getCRELLAContent,
    getCRELLAProbes,
} from '../../../api/authApisV2';
import { getSpeakingTestContent } from '../../../service/QuestionService';
import type { Prompt } from '../../../interface/Prompt';
import type { Probe } from '../../../interface/Probe';
import { fetchConfiguration, configurationsGT } from '../../../constants/configuration';
import { Configuration } from '../../../interface/Configuration';
import { Parts } from '../../../constants/constants';

const calibrationStatusMap: any = { GT: 'LIVE', GT_TRIAL: 'TRIAL' };
const isMiniVersion = String(process.env.REACT_APP_SHOW_MINI_VERSION) === 'true';

export const useLoadTestResource = ({ fetchOnLoad = true }: { fetchOnLoad?: boolean } = {}) => {
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [ratedContentFetched, setRatedContentFetched] = useState(false);
    const [promptList, setPromptList] = useState<Prompt[]>([]);
    const [probeList, setProbeList] = useState<Probe[]>([]);
    const [mediumPromptList, setMediumPromptList] = useState<Prompt[]>([]);
    const [hardPromptList, setHardPromptList] = useState<Prompt[]>([]);
    const [configuration, setConfiguration] = useState<Configuration>(configurationsGT);
    const [error, setError] = useState<string | null>(null);


    const { testTaker, insperaDetails, calibrationDetails, content, selectedTaskId } = useSelector((state: RootState) => state.appConfigSlice);

    const calibrationStatus = calibrationStatusMap[insperaDetails.product];
    const productType = insperaDetails?.product === 'GT_TRIAL' ? 'GT' : insperaDetails?.product;
    const globalReferenceId = insperaDetails?.globalReferenceId ?? '';

    const fetchResources = async () => {
        setLoading(true);

        try {
            const [contentResponse, probesResponse, configuration] = await Promise.all([
                content ? { data: structuredClone(content) } :
                    insperaDetails.product === 'CRELLA'
                      ? getCRELLAContent(testTaker, insperaDetails.product, calibrationDetails.level ?? '')
                      : fetchContents(),
                insperaDetails.product === 'CRELLA'
                    ? getCRELLAProbes(testTaker, insperaDetails.product, calibrationDetails.level ?? '')
                    :getProbes(testTaker, globalReferenceId, productType, calibrationStatus),
                fetchConfiguration(insperaDetails.product),
            ]);

            await saveHistory({
                testTakerDetails: testTaker,
                globalReferenceId,
                productType,
                atcAllocationDetail: contentResponse?.data?.atcAllocationDetail,
            });

            const probeList: Probe[] = probesResponse.data.probes.sort( //NOSONAR
                (a: Probe, b: Probe) => (a.sequence > b.sequence ? 1 : -1),
            );

            const list: Prompt[] = await getSpeakingTestContent(contentResponse.data);

            const medPromptList = list.filter((prompt) => prompt.partName === Parts.PART_3 && ( prompt.frame === 'FRAME_F' || prompt.frame === 'PART_3_OUTRO_FRAME' ));
            const harPromptList = list.filter((prompt) => prompt.partName === Parts.PART_3 && ( prompt.frame === 'FRAME_G' || prompt.frame === 'PART_3_OUTRO_FRAME' ));
            const filteredPromptList = list.filter((prompt) =>
                prompt.partName !== Parts.PART_3 || (prompt.partName === Parts.PART_3 && prompt.frame !== 'FRAME_F' && prompt.frame !== 'FRAME_G' && prompt.frame !== 'PART_3_OUTRO_FRAME')
            );
            
            setMediumPromptList(medPromptList);
            setHardPromptList(harPromptList);
            setPromptList(filteredPromptList);
            setProbeList(probeList);
            setConfiguration(configuration);

            setInitialized(true);
        } catch (error) {
            setError((error as Error).message ?? 'Error fetching test resources');
        } finally {
            setLoading(false);
        }
    };

    const fetchContents = async () => {
        if (selectedTaskId) {
            const response = await getPreviewContentById(testTaker, selectedTaskId);
            return response;
        }

        const fnCall = isMiniVersion ? getLiteVersionContentData : getContentData;
        const contentResponse = await fnCall(testTaker, productType, globalReferenceId, calibrationStatus);
        return contentResponse;
    };

    const getPart3RatedContent = () => {
        const rating = getRating();
        const ratedContent = rating > 5 ? hardPromptList : mediumPromptList;

        setPromptList([...promptList, ...ratedContent]);
        setRatedContentFetched(true);
    };

    useEffect(() => {
        if (testTaker?.testTakerId && fetchOnLoad) fetchResources();
    }, [testTaker?.testTakerId, fetchOnLoad]);

    return {
        fetchContents,
        testTaker,
        loading,
        initialized,
        promptList,
        probeList,
        configuration,
        error,
        ratedContentFetched,
        getPart3RatedContent,
        selectedTaskId,
    };
};
